'use client'

import { useState } from 'react'
import { BellOff, BellRing, Globe2, MoreHorizontal, Trash2 } from 'lucide-react'
import type { ICompanyDomain } from '@/api/organization/company-domain/get-company-domain-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import {
    getClaimedCompanyDomainEnrollmentLabel,
    getTurnOffCompanyDomainActionLabel,
    getTurnOnCompanyDomainActionLabel,
} from '@/features/settings-modal/tabs/organization/utils/company-domain-management'
import { cn } from '@/utils/styles/cn'

interface ICompanyDomainRowProps {
    domain: ICompanyDomain['domains'][number]
    canManage: boolean
    isBusy: boolean
    onTurnOnSuggestions: (domain: ICompanyDomain['domains'][number]) => void
    onTurnOffSuggestions: (domain: ICompanyDomain['domains'][number]) => void
    onRemove: (domain: ICompanyDomain['domains'][number]) => void
}

/** Shows the claimed company's real favicon so each row reads as an identity, not a config line. */
function DomainFavicon({ domain }: { domain: string }) {
    const [failed, setFailed] = useState(false)

    return (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-stone-200/80 bg-white shadow-xs">
            {failed ? (
                <Globe2 className="h-4 w-4 text-stone-400" aria-hidden="true" />
            ) : (
                <img
                    src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`}
                    alt=""
                    width={18}
                    height={18}
                    className="rounded-[4px]"
                    onError={() => setFailed(true)}
                />
            )}
        </div>
    )
}

/** Colored dot chips carry state severity: emerald = healthy, amber = needs attention, stone = neutral config. */
function StatusChip({ tone, children }: { tone: 'positive' | 'attention' | 'neutral'; children: React.ReactNode }) {
    return (
        <Badge
            variant="outline"
            className={cn(
                'shrink-0 gap-1.5 rounded-full px-2.5 py-1 font-medium',
                tone === 'positive' && 'border-emerald-200/80 bg-emerald-50 text-emerald-700',
                tone === 'attention' && 'border-amber-200/80 bg-amber-50 text-amber-800',
                tone === 'neutral' && 'border-stone-200/80 bg-stone-100/80 text-stone-600'
            )}
        >
            {tone !== 'neutral' && (
                <span
                    aria-hidden="true"
                    className={cn(
                        'h-1.5 w-1.5 shrink-0 rounded-full',
                        tone === 'positive' ? 'bg-emerald-500' : 'bg-amber-500'
                    )}
                />
            )}
            {children}
        </Badge>
    )
}

/** Renders one exact claimed Clerk domain and its server-authorized actions. */
export function CompanyDomainRow({
    domain,
    canManage,
    isBusy,
    onTurnOnSuggestions,
    onTurnOffSuggestions,
    onRemove,
}: ICompanyDomainRowProps) {
    const hasActions =
        domain.actions.canTurnOnSuggestions || domain.actions.canTurnOffSuggestions || domain.actions.canRemove
    const needsSetupRetry =
        domain.enrollmentMode !== 'manual_invitation' &&
        domain.automaticClaimStatus !== 'enabled' &&
        domain.actions.canTurnOnSuggestions
    const enrollmentLabel = getClaimedCompanyDomainEnrollmentLabel(domain)
    const isVerified = domain.verificationStatus === 'verified'

    return (
        <div className="group flex min-h-16 items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-stone-50/60">
            <div className="flex min-w-0 items-center gap-3">
                <DomainFavicon domain={domain.name} />
                <span className="min-w-0 truncate text-sm font-medium text-foreground">{domain.name}</span>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
                <StatusChip tone={isVerified ? 'positive' : 'attention'}>
                    {isVerified ? 'Verified' : 'Verification needed'}
                </StatusChip>
                <StatusChip tone={enrollmentLabel === 'Retry needed' ? 'attention' : 'neutral'}>
                    {enrollmentLabel}
                </StatusChip>

                {/* Always mounted: hiding it while actions refresh causes a layout shift. */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-stone-400 transition-colors hover:text-stone-700"
                            disabled={!canManage || isBusy || !hasActions}
                            aria-label={`Actions for ${domain.name}`}
                        >
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    {hasActions && (
                        <DropdownMenuContent
                            align="end"
                            className="min-w-[220px] rounded-xl border-stone-100 p-1 shadow-lg"
                        >
                            {needsSetupRetry && (
                                <DropdownMenuItem
                                    onSelect={() => onTurnOnSuggestions(domain)}
                                    className="rounded-lg text-stone-600 data-highlighted:bg-stone-50 data-highlighted:text-stone-800"
                                >
                                    <BellRing className="mr-1 h-3.5 w-3.5" />
                                    {getTurnOnCompanyDomainActionLabel(domain)}
                                </DropdownMenuItem>
                            )}
                            {domain.actions.canTurnOffSuggestions && (
                                <DropdownMenuItem
                                    onSelect={() => onTurnOffSuggestions(domain)}
                                    className="rounded-lg text-stone-600 data-highlighted:bg-stone-50 data-highlighted:text-stone-800"
                                >
                                    <BellOff className="mr-1 h-3.5 w-3.5" />
                                    {getTurnOffCompanyDomainActionLabel(domain.enrollmentMode)}
                                </DropdownMenuItem>
                            )}
                            {domain.actions.canTurnOnSuggestions && !needsSetupRetry && (
                                <DropdownMenuItem
                                    onSelect={() => onTurnOnSuggestions(domain)}
                                    className="rounded-lg text-stone-600 data-highlighted:bg-stone-50 data-highlighted:text-stone-800"
                                >
                                    <BellRing className="mr-1 h-3.5 w-3.5" />
                                    {getTurnOnCompanyDomainActionLabel(domain)}
                                </DropdownMenuItem>
                            )}
                            {domain.actions.canRemove && (
                                <DropdownMenuItem
                                    onSelect={() => onRemove(domain)}
                                    className="rounded-lg text-red-600 data-highlighted:bg-red-50 data-highlighted:text-red-700"
                                >
                                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                                    Remove domain
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    )}
                </DropdownMenu>
            </div>
        </div>
    )
}
